import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { X, Upload, Download, FileText, CheckCircle, XCircle, DollarSign, Loader2, Trash2, Layers, Plus } from 'lucide-react';
import { api, AED, fileUrl } from '../lib/api';
import { useAuth } from '../lib/auth';
import { toast } from 'sonner';

export default function InvoiceModal({ invoiceId, onClose, onUpdate }) {
  const { user: me } = useAuth();
  const [inv, setInv] = useState(null);
  const [vendor, setVendor] = useState(null);
  const [property, setProperty] = useState(null);
  const [users, setUsers] = useState({});
  const [uploading, setUploading] = useState(false);
  const [acting, setActing] = useState(null);  // 'approve' | 'reject' | 'pay'
  const [note, setNote] = useState('');
  const [payRef, setPayRef] = useState('');
  const [showPayForm, setShowPayForm] = useState(false);
  const [refData, setRefData] = useState(null);   // BOQ lines available for allocation
  const [allocations, setAllocations] = useState([]);
  const [allocEditing, setAllocEditing] = useState(false);
  const [allocSaving, setAllocSaving] = useState(false);
  // Retro-VAT — lets users apply / change VAT after invoice creation
  const [vatEditOpen, setVatEditOpen] = useState(false);
  const [vatForm, setVatForm] = useState({ apply_vat: false, vat_inclusive: false, amount_aed: 0 });
  const [vatSaving, setVatSaving] = useState(false);

  const openVatEditor = () => {
    setVatForm({
      apply_vat: (inv.vat_aed || 0) > 0,
      vat_inclusive: !!inv.vat_inclusive,
      amount_aed: inv.amount_aed || 0,
    });
    setVatEditOpen(true);
  };
  const saveVat = async () => {
    setVatSaving(true);
    try {
      await api.post(`/invoices/${invoiceId}/apply-vat`, {
        apply_vat: vatForm.apply_vat,
        vat_inclusive: vatForm.vat_inclusive,
        amount_aed: parseFloat(vatForm.amount_aed) || 0,
      });
      toast.success('VAT recalculated');
      setVatEditOpen(false);
      await load();
      onUpdate?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to recalculate VAT');
    } finally { setVatSaving(false); }
  };

  const load = async () => {
    const r = await api.get(`/invoices/${invoiceId}`);
    setInv(r.data);
    setAllocations(r.data.boq_line_allocations || []);
    const [v, p, u, rf] = await Promise.all([
      r.data.vendor_id ? api.get(`/vendors/${r.data.vendor_id}`) : Promise.resolve({ data: null }),
      r.data.property_id ? api.get(`/properties/${r.data.property_id}`) : Promise.resolve({ data: null }),
      api.get('/users').catch(() => ({ data: [] })),
      api.get(`/invoices/${invoiceId}/reference-data`).catch(() => ({ data: null })),
    ]);
    setVendor(v.data); setProperty(p.data);
    setUsers(Object.fromEntries((u.data || []).map(x => [x.user_id, x])));
    setRefData(rf.data);
  };
  useEffect(() => { if (invoiceId) load(); }, [invoiceId]);

  const uploadFile = async (e, kind) => {
    const f = e.target.files?.[0]; if (!f) return;
    setUploading(true);
    try {
      const form = new FormData(); form.append('file', f);
      form.append('property_id', inv.property_id);
      form.append('category', kind === 'receipt' ? 'payment_receipt' : 'invoice');
      form.append('title', kind === 'receipt'
        ? `Payment receipt ${inv.invoice_number}`
        : `Invoice ${inv.invoice_number}`);
      const r = await api.post('/files/upload', form, { headers: { 'Content-Type': 'multipart/form-data' } });
      const patch = kind === 'receipt'
        ? { payment_receipt_file_id: r.data.file_id }
        : { file_id: r.data.file_id };
      await api.patch(`/invoices/${invoiceId}`, patch);
      await load();
      onUpdate?.();
    } finally { setUploading(false); }
  };

  const act = async (action, extra = {}) => {
    setActing(action);
    try {
      await api.post(`/invoices/${invoiceId}/approve`, { action, note, ...extra });
      toast.success(action === 'reject' ? 'Invoice rejected' :
                    extra.payment_reference !== undefined ? 'Payment recorded' :
                    'Invoice approved · advanced to next step');
      setNote(''); setPayRef(''); setShowPayForm(false);
      await load();
      onUpdate?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Action failed');
    } finally { setActing(null); }
  };

  const doDelete = async () => {
    const paidWarn = inv.status === 'paid'
      ? `⚠️ This invoice is PAID. Its Payment record (${AED(inv.net_payable_aed || inv.amount_aed)}) will also be removed, `
        + `and the vendor's paid-total in reconciliation will drop by this amount.\n\n`
      : '';
    if (!window.confirm(
      `${paidWarn}Delete invoice ${inv.invoice_number}?\n\nThis cannot be undone.`
    )) return;
    setActing('delete');
    try {
      const r = await api.delete(`/invoices/${invoiceId}`);
      toast.success(r.data?.payments_removed
        ? `Invoice deleted · ${r.data.payments_removed} payment record(s) also removed`
        : 'Invoice deleted');
      onUpdate?.();
      onClose?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Delete failed');
    } finally { setActing(null); }
  };

  const saveAllocations = async () => {
    // Trim empty rows
    const clean = allocations
      .filter(a => a.boq_line_id && parseFloat(a.amount_aed) > 0)
      .map(a => ({ boq_line_id: a.boq_line_id, amount_aed: parseFloat(a.amount_aed) }));
    const total = clean.reduce((s, a) => s + a.amount_aed, 0);
    if (total > (inv?.amount_aed || 0) + 1) {
      toast.error(`Total allocations (${AED(total)}) exceed invoice amount (${AED(inv.amount_aed)})`);
      return;
    }
    setAllocSaving(true);
    try {
      await api.post(`/invoices/${invoiceId}/allocations`, { allocations: clean });
      toast.success('Line allocations saved');
      setAllocEditing(false);
      await load();
      onUpdate?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save allocations');
    } finally { setAllocSaving(false); }
  };

  if (!inv) return null;

  const statusClass = inv.status === 'paid' ? 'status-success' : inv.status === 'rejected' ? 'status-danger' : 'status-warning';

  // Can this user act on this invoice at its current step?
  // Backend rule: owner OR role matching current_step
  const roleMatchesStep = me && (
    me.role === 'owner' ||
    me.role === inv.current_step ||
    // Fallback aliases some deployments use
    (inv.current_step === 'project_manager' && me.role === 'pm')
  );
  const canAct = !!roleMatchesStep && !['paid', 'rejected'].includes(inv.status);

  const canDelete = !!me && (
    me.role === 'owner' || me.role === 'md' ||
    (inv.submitted_by && inv.submitted_by === me.user_id)
  );

  const stepLabel = {
    project_manager: 'Project Manager',
    md: 'Managing Director',
    payments: 'Payments team',
  }[inv.current_step] || inv.current_step;

  const nextStepLabel = {
    project_manager: 'Managing Director',
    md: 'Payments team',
    payments: 'Marked paid ✓',
  }[inv.current_step];

  return (
    <div className="fixed inset-0 z-50 grid place-items-center p-4 bg-slate-900/40 backdrop-blur-sm" onClick={onClose} data-testid="invoice-modal">
      <motion.div initial={{opacity:0,y:20}} animate={{opacity:1,y:0}} onClick={(e)=>e.stopPropagation()}
                  className="glass-strong w-full max-w-2xl max-h-[90vh] overflow-y-auto p-8">
        <div className="flex items-start justify-between mb-6">
          <div>
            <div className="overline">Invoice</div>
            <div className="font-serif text-3xl">{inv.invoice_number}</div>
            <div className="text-sm text-slate-500 mt-1">{property?.title || '—'} · {vendor?.company || '—'}</div>
          </div>
          <div className="flex items-center gap-3">
            <span className={`status-pill ${statusClass}`}>{inv.status?.replace(/_/g, ' ')}</span>
            {canDelete && (
              <button onClick={doDelete} disabled={!!acting}
                       data-testid="invoice-delete-btn"
                       title={inv.status === 'paid'
                         ? 'Delete invoice (will also remove linked Payment record)'
                         : 'Delete invoice'}
                       className="p-2 hover:bg-red-50 rounded-full text-red-600">
                {acting === 'delete' ? <Loader2 size={16} className="animate-spin"/> : <Trash2 size={16}/>}
              </button>
            )}
            <button onClick={onClose} className="p-2 hover:bg-white/60 rounded-full"><X size={18}/></button>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 mb-4">
          <div><div className="overline">Amount</div><div className="font-serif text-2xl mt-1">{AED(inv.amount_aed)}</div></div>
          <div><div className="overline">Net payable</div><div className="font-serif text-2xl mt-1">{AED(inv.net_payable_aed)}</div></div>
          <div>
            <div className="overline">VAT{inv.vat_rate_pct != null ? ` (${inv.vat_rate_pct}%)` : ''}</div>
            <div className="text-sm mt-1">{AED(inv.vat_aed)}
              {inv.vat_inclusive != null && inv.vat_aed > 0 && (
                <span className="ml-1 text-[10px] text-slate-500">
                  {inv.vat_inclusive ? '· gross-of-VAT input' : '· net + VAT'}
                </span>
              )}
            </div>
          </div>
          <div><div className="overline">Retention held</div><div className="text-sm mt-1">{AED(inv.retention_held_aed)}</div></div>
        </div>

        {/* Retro-VAT — visible until PAID. Lets users apply/change VAT after
            creation without deleting the invoice. Disabled once paid because
            recalculating VAT on a settled invoice would break the cashflow. */}
        {inv.status !== 'paid' && (
          <div className="mb-6 p-3 rounded-2xl bg-slate-50/60 border border-slate-200">
            {!vatEditOpen ? (
              <button onClick={openVatEditor} data-testid="invoice-vat-toggle"
                      className="text-xs text-[#679286] hover:underline">
                {(inv.vat_aed || 0) > 0 ? 'Adjust VAT' : 'Apply VAT to this invoice'}
              </button>
            ) : (
              <div className="space-y-2">
                <div className="overline">Recalculate VAT</div>
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={vatForm.apply_vat}
                          onChange={(e) => setVatForm({ ...vatForm, apply_vat: e.target.checked })}
                          data-testid="invoice-vat-apply"
                          className="w-4 h-4 accent-[#679286]"/>
                  <span>This invoice includes VAT (auto-calculate)</span>
                </label>
                {vatForm.apply_vat && (
                  <label className="flex items-center gap-2 text-xs text-slate-600 ml-6">
                    <input type="checkbox" checked={vatForm.vat_inclusive}
                            onChange={(e) => setVatForm({ ...vatForm, vat_inclusive: e.target.checked })}
                            data-testid="invoice-vat-inclusive"
                            className="w-3.5 h-3.5 accent-[#679286]"/>
                    <span>Amount above is already gross-of-VAT (split out). Untick if amount is net.</span>
                  </label>
                )}
                <div className="flex items-center gap-2 pt-1">
                  <label className="text-xs text-slate-600 flex items-center gap-2">
                    Amount:
                    <input type="number" min="0" step="0.01"
                            value={vatForm.amount_aed}
                            onChange={(e) => setVatForm({ ...vatForm, amount_aed: e.target.value })}
                            data-testid="invoice-vat-amount"
                            className="w-32 px-2 py-1 rounded-lg border border-slate-200 bg-white text-xs font-mono"/>
                  </label>
                  <button onClick={saveVat} disabled={vatSaving}
                          data-testid="invoice-vat-save"
                          className="btn-primary text-xs inline-flex items-center gap-1.5">
                    {vatSaving ? <Loader2 size={12} className="animate-spin"/> : <CheckCircle size={12}/>}
                    Save
                  </button>
                  <button onClick={() => setVatEditOpen(false)} className="btn-ghost text-xs">Cancel</button>
                </div>
              </div>
            )}
          </div>
        )}

        <div className="mb-6">
          <div className="overline mb-1">Description</div>
          <div className="text-sm">{inv.description}</div>
        </div>

        <div className="mb-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
          {/* Invoice PDF (the vendor's bill) */}
          <div>
            <div className="overline mb-2">Invoice PDF</div>
            {inv.file_id ? (
              <a href={fileUrl(inv.file_id)} target="_blank" rel="noreferrer" data-testid="invoice-file-link"
                 className="inline-flex items-center gap-2 px-4 py-2 bg-white/70 rounded-full border border-white/70 text-sm hover:bg-white">
                <FileText size={14}/> View invoice PDF <Download size={12}/>
              </a>
            ) : (
              <label className="inline-flex items-center gap-2 px-4 py-2 bg-white/70 rounded-full border border-white/70 text-sm cursor-pointer hover:bg-white" data-testid="upload-invoice-file">
                <Upload size={14}/> {uploading ? 'Uploading…' : 'Attach invoice PDF'}
                <input type="file" className="hidden" onChange={(e) => uploadFile(e, 'invoice')} accept=".pdf,image/*"/>
              </label>
            )}
          </div>
          {/* Payment receipt (bank transfer slip / cheque photo) — only
              meaningful once the invoice is at/past the payments step. */}
          <div>
            <div className="overline mb-2">Payment receipt</div>
            {inv.payment_receipt_file_id ? (
              <a href={fileUrl(inv.payment_receipt_file_id)} target="_blank" rel="noreferrer" data-testid="payment-receipt-link"
                 className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-50 rounded-full border border-emerald-200 text-sm hover:bg-emerald-100">
                <FileText size={14}/> View payment receipt <Download size={12}/>
              </a>
            ) : (
              <label className="inline-flex items-center gap-2 px-4 py-2 bg-white/70 rounded-full border border-white/70 text-sm cursor-pointer hover:bg-white" data-testid="upload-payment-receipt">
                <Upload size={14}/> {uploading ? 'Uploading…' : 'Attach payment receipt'}
                <input type="file" className="hidden" onChange={(e) => uploadFile(e, 'receipt')} accept=".pdf,image/*"/>
              </label>
            )}
          </div>
        </div>

        {/* Approval action bar */}
        {!['paid', 'rejected'].includes(inv.status) && (
          <div className="mb-6 p-4 rounded-2xl border border-amber-200 bg-amber-50/50">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <div>
                <div className="overline">Awaiting action</div>
                <div className="text-sm font-medium mt-1">{stepLabel} must {inv.current_step === 'payments' ? 'record payment' : 'approve or reject'}</div>
                <div className="text-xs text-slate-500 mt-0.5">
                  Next: <span className="font-medium text-slate-700">{nextStepLabel}</span>
                </div>
              </div>
              {!canAct && (
                <div className="text-xs text-amber-800 max-w-xs text-right">
                  Only the {stepLabel} (or an owner) can act on this invoice.
                </div>
              )}
            </div>

            {canAct && (
              <>
                {inv.current_step === 'payments' && showPayForm && (
                  <div className="mb-3 space-y-3">
                    {/* Attach / re-attach the payment receipt right here — so
                        Payments team doesn't have to scroll back up. */}
                    <div>
                      <label className="text-xs uppercase tracking-widest text-slate-500 block mb-1">
                        Payment receipt (bank transfer slip / cheque)
                      </label>
                      {inv.payment_receipt_file_id ? (
                        <div className="flex items-center gap-2 flex-wrap">
                          <a href={fileUrl(inv.payment_receipt_file_id)} target="_blank" rel="noreferrer"
                             data-testid="pay-panel-receipt-view"
                             className="inline-flex items-center gap-2 px-3 py-1.5 bg-emerald-50 rounded-full border border-emerald-200 text-xs hover:bg-emerald-100">
                            <FileText size={12}/> View attached receipt
                          </a>
                          <label data-testid="pay-panel-receipt-replace"
                                 className="inline-flex items-center gap-1.5 text-xs text-[#679286] hover:underline cursor-pointer">
                            <Upload size={11}/> Replace
                            <input type="file" className="hidden" onChange={(e) => uploadFile(e, 'receipt')} accept=".pdf,image/*"/>
                          </label>
                        </div>
                      ) : (
                        <label data-testid="pay-panel-receipt-upload"
                                className="inline-flex items-center gap-2 px-4 py-2 bg-white rounded-full border border-slate-200 text-xs cursor-pointer hover:bg-slate-50">
                          <Upload size={12}/> {uploading ? 'Uploading…' : 'Attach payment receipt'}
                          <input type="file" className="hidden" onChange={(e) => uploadFile(e, 'receipt')} accept=".pdf,image/*"/>
                        </label>
                      )}
                      {/* Also expose the invoice PDF for last-minute replacement */}
                      {!inv.file_id && (
                        <label data-testid="pay-panel-invoice-upload"
                                className="mt-2 inline-flex items-center gap-2 text-xs text-slate-500 hover:text-slate-900 cursor-pointer">
                          <Upload size={11}/> Missing invoice PDF — attach now
                          <input type="file" className="hidden" onChange={(e) => uploadFile(e, 'invoice')} accept=".pdf,image/*"/>
                        </label>
                      )}
                    </div>
                    <label className="text-xs uppercase tracking-widest text-slate-500 block">
                      Bank / payment reference (optional — auto-generated if blank)
                      <input value={payRef} onChange={(e)=>setPayRef(e.target.value)}
                             placeholder="e.g. NEFT-2026-08-01234"
                             data-testid="invoice-pay-ref"
                             className="mt-1 w-full px-3 py-2 rounded-xl border border-slate-200 bg-white/80 text-sm font-mono normal-case tracking-normal"/>
                    </label>
                  </div>
                )}
                <label className="text-xs uppercase tracking-widest text-slate-500 block mb-3">
                  Note (optional — attached to approval trail)
                  <textarea value={note} onChange={(e)=>setNote(e.target.value)}
                            rows={2} data-testid="invoice-action-note"
                            placeholder={inv.current_step === 'payments' ? 'e.g. Paid via HSBC business account' : 'e.g. Scope aligned with BOQ v2'}
                            className="mt-1 w-full px-3 py-2 rounded-xl border border-slate-200 bg-white/80 text-sm normal-case tracking-normal"/>
                </label>
                <div className="flex flex-wrap gap-2 justify-end">
                  <button onClick={() => act('reject')} disabled={!!acting} data-testid="invoice-reject-btn"
                          className="btn-ghost text-sm text-red-600 hover:text-red-700 hover:bg-red-50 inline-flex items-center gap-2">
                    {acting === 'reject' ? <Loader2 size={14} className="animate-spin"/> : <XCircle size={14}/>}
                    Reject
                  </button>
                  {inv.current_step === 'payments' ? (
                    !showPayForm ? (
                      <button onClick={() => setShowPayForm(true)} data-testid="invoice-mark-paid-btn"
                              className="btn-primary text-sm inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700">
                        <DollarSign size={14}/> Mark as paid
                      </button>
                    ) : (
                      <button onClick={() => act('approve', { payment_reference: payRef || undefined })}
                              disabled={!!acting} data-testid="invoice-confirm-paid-btn"
                              className="btn-primary text-sm inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700">
                        {acting === 'approve' ? <Loader2 size={14} className="animate-spin"/> : <CheckCircle size={14}/>}
                        Confirm payment
                      </button>
                    )
                  ) : (
                    <button onClick={() => act('approve')} disabled={!!acting} data-testid="invoice-approve-btn"
                            className="btn-primary text-sm inline-flex items-center gap-2">
                      {acting === 'approve' ? <Loader2 size={14} className="animate-spin"/> : <CheckCircle size={14}/>}
                      Approve · send to {nextStepLabel}
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        )}

        {/* BOQ line allocations */}
        {refData && (refData.lines?.length > 0 || (inv.boq_line_allocations || []).length > 0) && (
          <div className="mb-6 p-4 rounded-2xl border border-slate-200 bg-white/60">
            <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
              <div className="flex items-center gap-2">
                <Layers size={14} className="text-slate-500"/>
                <div className="overline">BOQ line allocations</div>
              </div>
              {inv.status !== 'paid' && inv.status !== 'rejected' && !allocEditing && (
                <button onClick={() => setAllocEditing(true)} data-testid="alloc-edit-btn"
                        className="text-xs text-[#679286] hover:underline">
                  {allocations.length ? 'Edit allocations' : 'Allocate to lines'}
                </button>
              )}
            </div>
            {!allocEditing ? (
              allocations.length === 0 ? (
                <div className="text-xs text-slate-500 italic">
                  No line allocations yet. Assign this invoice to specific BOQ line items to enable line-level reconciliation.
                </div>
              ) : (
                <div className="space-y-1.5">
                  {allocations.map((a, i) => {
                    const l = refData.lines?.find(x => x.id === a.boq_line_id);
                    return (
                      <div key={i} className="flex items-center justify-between text-xs px-2 py-1.5 bg-white/70 rounded-lg">
                        <div>
                          <div className="font-medium text-slate-800">{l?.description || <span className="italic text-slate-400">Deleted line</span>}</div>
                          <div className="text-[10px] text-slate-500 mt-0.5">
                            {l?.category}{l?.boq_is_final ? ' · Final BOQ' : l ? ' · Draft BOQ' : ''}
                          </div>
                        </div>
                        <span className="font-semibold">{AED(a.amount_aed)}</span>
                      </div>
                    );
                  })}
                  <div className="flex justify-between text-xs pt-2 mt-2 border-t border-slate-100">
                    <span className="text-slate-500">Total allocated</span>
                    <span className="font-semibold">{AED(allocations.reduce((s, a) => s + parseFloat(a.amount_aed || 0), 0))}</span>
                  </div>
                </div>
              )
            ) : (
              <div className="space-y-2">
                {allocations.map((a, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <select value={a.boq_line_id || ''}
                             onChange={(e) => {
                               const next = [...allocations];
                               next[i] = { ...next[i], boq_line_id: e.target.value };
                               setAllocations(next);
                             }}
                             data-testid={`alloc-line-select-${i}`}
                             className="flex-1 px-2 py-1.5 rounded-lg border border-slate-200 bg-white/80 text-xs">
                      <option value="">— Select BOQ line —</option>
                      {refData.lines.map(l => (
                        <option key={l.id} value={l.id}>
                          {l.boq_is_final ? '⭐ ' : ''}{l.category} · {l.description?.slice(0, 60)}{(l.description || '').length > 60 ? '…' : ''} · {AED(l.total_aed)}
                        </option>
                      ))}
                    </select>
                    <input type="number" min="0" step="0.01"
                            value={a.amount_aed}
                            onChange={(e) => {
                              const next = [...allocations];
                              next[i] = { ...next[i], amount_aed: e.target.value };
                              setAllocations(next);
                            }}
                            data-testid={`alloc-amount-${i}`}
                            placeholder="AED"
                            className="w-28 px-2 py-1.5 rounded-lg border border-slate-200 bg-white/80 text-xs font-mono"/>
                    <button onClick={() => setAllocations(allocations.filter((_, j) => j !== i))}
                            className="p-1.5 hover:bg-red-50 rounded-full text-red-500"
                            data-testid={`alloc-remove-${i}`}>
                      <Trash2 size={12}/>
                    </button>
                  </div>
                ))}
                <button onClick={() => setAllocations([...allocations, { boq_line_id: '', amount_aed: '' }])}
                        data-testid="alloc-add-line-btn"
                        className="text-xs text-[#679286] hover:underline inline-flex items-center gap-1">
                  <Plus size={12}/> Add line
                </button>
                <div className="flex justify-between text-xs pt-2 mt-2 border-t border-slate-100">
                  <span className="text-slate-500">
                    Total allocated
                    <span className="ml-2 text-slate-400">/ {AED(inv.amount_aed)}</span>
                  </span>
                  <span className="font-semibold">
                    {AED(allocations.reduce((s, a) => s + (parseFloat(a.amount_aed) || 0), 0))}
                  </span>
                </div>
                <div className="flex justify-end gap-2 pt-2">
                  <button onClick={() => { setAllocEditing(false); setAllocations(inv.boq_line_allocations || []); }}
                          className="btn-ghost text-xs">Cancel</button>
                  <button onClick={saveAllocations} disabled={allocSaving}
                          data-testid="alloc-save-btn"
                          className="btn-primary text-xs inline-flex items-center gap-1.5">
                    {allocSaving ? <Loader2 size={12} className="animate-spin"/> : <CheckCircle size={12}/>}
                    Save allocations
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        <div className="mb-4">
          <div className="overline mb-3">Approval trail</div>          <div className="space-y-2">
            {(inv.approvals || []).map((a, i) => (
              <div key={i} className="flex items-center gap-3 p-2 bg-white/50 rounded-xl">
                {a.action === 'approve' || a.action === 'submit' || a.action === 'paid'
                  ? <CheckCircle size={14} className="text-emerald-600"/>
                  : <XCircle size={14} className="text-red-500"/>}
                <div className="flex-1">
                  <div className="text-sm font-medium capitalize">{a.step?.replace(/_/g, ' ')} — {a.action}</div>
                  <div className="text-xs text-slate-500">{users[a.user_id]?.name || 'System'} · {new Date(a.at).toLocaleString()}</div>
                  {a.note && <div className="text-xs text-slate-600 mt-1 italic">&ldquo;{a.note}&rdquo;</div>}
                </div>
              </div>
            ))}
          </div>
        </div>

        {inv.status === 'paid' && inv.payment_reference && (
          <div className="p-3 bg-emerald-50 rounded-xl border border-emerald-200 text-sm">
            Paid · reference <span className="font-mono">{inv.payment_reference}</span> · {new Date(inv.paid_at).toLocaleDateString()}
          </div>
        )}
      </motion.div>
    </div>
  );
}
